import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge, Box, Flex, NavLink, Table } from '@mantine/core'
import { IconChevronLeft } from '@tabler/icons-react'
import dayjs from 'dayjs'

import { GoalStepsForm } from '@/modules/actionPlan/pages/components/GoalStepsForm/GoalStepsForm'
import { GoalStepsList } from '@/modules/actionPlan/pages/components/GoalStepsList/GoalStepsList'
import { ActionPlanRoutePathsEnum } from '@/modules/actionPlan/routes/actionPlanRouteConfig.types'

import { Button } from '@/components/Button/Button'
import { Loader } from '@/components/Loader/Loader'
import { Progress } from '@/components/Progress/Progress'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import { useGoalsServiceGoalsControllerGet } from '@/__generated__/queries'
import type { GoalStepData } from '@/__generated__/requests'

import { useGoalSteps } from '@/hooks/useGoalSteps.hook'

import { formatMoney } from '@/helpers/formatMoney.helper'

import classes from './GoalDetail.module.css'

const STATUS_BADGE_MAP: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: 'green', label: 'Active' },
  ARCHIVED: { color: 'gray', label: 'Archived' },
  COMPLETED: { color: 'blue', label: 'Completed' },
}

export function GoalDetailPage() {
  const { goalId } = useParams<{ goalId: string }>()
  const navigate = useNavigate()
  const [steps, setSteps] = useState<GoalStepData[]>([])

  const { data: goal, isLoading } = useGoalsServiceGoalsControllerGet(
    { goalId: Number(goalId) },
    undefined,
    { enabled: !!goalId },
  )

  const { data: fetchedSteps } = useGoalSteps(Number(goalId), !!goalId)

  useEffect(() => {
    if (fetchedSteps) {
      setSteps([...fetchedSteps].sort((a, b) => a.sortOrder - b.sortOrder))
    }
  }, [fetchedSteps])

  const handleStepAdded = useCallback((step: GoalStepData) => {
    setSteps(prev => [...prev, step].sort((a, b) => a.sortOrder - b.sortOrder))
  }, [])

  if (isLoading) {
    return (
      <Flex justify="center" mt={48}>
        <Loader isMinimized />
      </Flex>
    )
  }

  if (!goal) {
    return (
      <Box mt={48} ta="center">
        <Text variant="body_2_semibold_black">Goal not found</Text>
        <Button
          mt={16}
          onClick={() => navigate(ActionPlanRoutePathsEnum.ACTION_PLAN)}
          variant="outline"
        >
          Back to action plan
        </Button>
      </Box>
    )
  }

  const percentageValue = goal.amount
    ? Math.floor(((goal.collectedAmount || 0) / goal.amount) * 100)
    : 0

  const badge = STATUS_BADGE_MAP[goal.status]
  const isActive = goal.status === 'ACTIVE'

  return (
    <Box>
      <NavLink
        className={classes.navlink}
        label={
          <Flex gap={4}>
            <IconChevronLeft size={20} />
            Back to list
          </Flex>
        }
        mb={20}
        onClick={() => navigate(ActionPlanRoutePathsEnum.ACTION_PLAN)}
      />

      <Box
        mb={24}
        p={24}
        style={{
          backgroundColor: 'var(--mantine-other-colors-white)',
          borderRadius: 8,
          boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Flex align="center" gap={12} mb={16}>
          <Title mb={0} variant="h1">
            {goal.name}
          </Title>
          {badge && (
            <Badge color={badge.color} size="lg" variant="light">
              {badge.label}
            </Badge>
          )}
        </Flex>

        <ScrollArea scrollbars="x">
          <Table>
            <Table.Thead>
              <Table.Tr
                style={{
                  borderColor: 'var(--mantine-other-colors-light-grey)',
                }}
              >
                <Table.Th style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_1_medium_green">Start date</Text>
                </Table.Th>
                <Table.Th style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_1_medium_green">Due date</Text>
                </Table.Th>
                <Table.Th style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_1_medium_green">Final goal</Text>
                </Table.Th>
                <Table.Th style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_1_medium_green">Progress</Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr
                style={{
                  borderColor: 'var(--mantine-other-colors-light-grey)',
                }}
              >
                <Table.Td miw={140} style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_2_medium_black">
                    {dayjs(goal.startDate).format('MMM D, YYYY')}
                  </Text>
                </Table.Td>
                <Table.Td miw={140} style={{ padding: '12px 24px 12px 0' }}>
                  <Text variant="body_2_medium_black">
                    {dayjs(goal.dueDate).format('MMM D, YYYY')}
                  </Text>
                </Table.Td>
                <Table.Td miw={140} style={{ padding: '12px 24px 12px 0' }}>
                  <Flex>
                    <Text variant="body_2_semibold_grey">
                      {formatMoney(goal.collectedAmount)}/
                    </Text>
                    <Text variant="body_2_semibold_black">
                      {formatMoney(goal.amount)}
                    </Text>
                  </Flex>
                </Table.Td>
                <Table.Td miw={250} style={{ padding: '12px 24px 12px 0' }}>
                  <Flex align="center" gap={16}>
                    <Progress value={percentageValue} w="100%" />
                    <Text variant="body_2_semibold_black">
                      {percentageValue}%
                    </Text>
                  </Flex>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Box>

      <Flex direction={{ base: 'column', md: 'row' }} gap={24}>
        <Box flex={1}>
          <GoalStepsList
            goalId={goal.id}
            isActive={isActive}
            onStepsChange={setSteps}
            steps={steps}
          />
        </Box>
        {isActive && (
          <Box flex={1}>
            <GoalStepsForm goalId={goal.id} onStepAdded={handleStepAdded} />
          </Box>
        )}
      </Flex>
    </Box>
  )
}
